<template>
  <section :class="['expandable-item', { 'expandable-item--is-open': isOpen }, themeClass ]">
    <h2 class="expandable-item__heading">
      <button :class="['expandable-item__toggler', { 'expandable-item__toggler--is-open': openHeading && isOpen }]" @click="handleToggleAccordion()">
        {{ headingText() }}

        <slot name="icon" :isOpen="isOpen">
          <oie-use-svg
            svg="circle-down"
            :class="[{'svg--180-negative': isOpen}]"
            :height="svgSize"
            :width="svgSize"></oie-use-svg>
        </slot>
      </button>
    </h2>

    <div class="expandable-item__pocket">
      <slot name="pocket"></slot>
    </div>

    <div :class="['expandable-item__content']">
      <slot></slot>
    </div>
  </section>
</template>

<script>
import accordionMixin from '@/mixins/accordion.js'
import nextStepMixin from '@/mixins/checkout-next-step.js'
import _ from 'lodash'

const themes = ['primary', 'secondary', 'tertiary']

export default {
  mixins: [accordionMixin, nextStepMixin],
  props: {
    heading: {
      type: String,
      required: false,
      default: ''
    },
    openHeading: {
      type: String,
      default: null
    },
    theme: {
      type: String,
      required: false,
      default: null,
      validator: (val) => _.includes(themes, val)
    }
  },
  computed: {
    svgSize() { return this.mq('small') ? '30' : '20' },
    themeClass() { return this.theme && `expandable-item--${this.theme}` }
  },
  mounted() {
    if (this.isActiveSection || this.openOnInit) {
      this.isOpen = true
    }
  },
  methods: {
    headingText() {
      return this.isOpen && this.openHeading ? this.openHeading : this.heading
    },
    handleToggleAccordion() {
      this.toggleAccordion()
      this.$emit('toggle-accordion', this.isOpen)
    }
  }
}
</script>

<style lang="scss">
/** General styles */
.expandable-item {
  position: relative;
  background-color: color(background-white);
  border: 1px solid color(border-gray);
}

.expandable-item--is-superuser {
  border: 4px solid color(purple-1);
}

.expandable-item--is-disabled {
  opacity: .5;
  pointer-events: none;
}

/** Heading styles */
.expandable-item__heading {
  margin: 0;
  padding: 0;
}

/** Toggler styles */
.expandable-item__toggler {
  @include reset-button;
  @include body-semi-bold;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: measure(half) ;

  @include media(medium) {
    @include h2-semi-bold;
  }

  @include media(small) {
    padding: measure(full) ;
  }
}

/** Svg styles */
.expandable-item__toggler svg {
  color: color(gray-2);
  flex-shrink: 0;

   @media (prefers-reduced-motion: no-preference) {
    transition: all .2s ease;
  }
}

/** Pocket styles */
.expandable-item__pocket {
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
  align-items: flex-start;
  margin: 0 measure(half) measure(half) measure(half);

  @include media(small) {
    margin: 0 measure(full) measure(full) measure(full);
  }
}

.expandable-item__pocket:empty {
  display: none;
}

/** Content styles */
.expandable-item__content {
  display: none;
  padding: measure(half);

  @include media(small) {
    padding: measure(full);
  }
}

.expandable-item--is-open > .expandable-item__content {
  display: flex;
  flex-direction: column;
}

// REFACTOR - respinsibility of content presentation should not be here
.expandable-item--seperate-items .expandable-item__content > *:not(:last-child) {
  max-width: initial;
  margin-bottom: measure(half);
  padding-bottom: measure(half);
  border-bottom: 1px solid color(border-gray);

  @include media(small) {
    margin-bottom: measure(full);
    padding-bottom: measure(full);
  }
}

/** Theme styles primary */
.expandable-item.expandable-item--primary > .expandable-item__heading .expandable-item__toggler {
  @include body;
}

.expandable-item.expandable-item--primary > .expandable-item__heading .expandable-item__toggler,
.expandable-item.expandable-item--primary > .expandable-item__content {
  padding: measure(quarter);
}

/** Theme styles secondary */
.expandable-item--secondary {
  background-color: color(blue-4);
  border: none;
}

.expandable-item.expandable-item--secondary > .expandable-item__heading .expandable-item__toggler,
.expandable-item.expandable-item--secondary > .expandable-item__content {
  padding: 1rem 1.5rem; // we should be using standard measures....
}

.expandable-item--secondary > .expandable-item__heading .expandable-item__toggler{
  @include body-semi-bold;
}

.expandable-item--secondary.expandable-item--is-open > .expandable-item__heading .expandable-item__toggler  {
  @include h2-semi-bold;
}

.expandable-item--secondary .section-block--create-space{
  margin-bottom: 0;
}

/** Theme styles tertiary */
.expandable-item--tertiary {
  border: none;
}

.expandable-item.expandable-item--tertiary > .expandable-item__heading .expandable-item__toggler,
.expandable-item.expandable-item--tertiary > .expandable-item__content {
  padding: measure(quarter) 0
}

.expandable-item--tertiary > .expandable-item__heading .expandable-item__toggler{
  @include body-semi-bold;
}

</style>
